/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react'

import { Icon, Icons } from '@clientbase/clientbase-library'
import { Box, Skeleton, Stack, Tooltip } from '@mui/material'
import { ParsedUrlQuery } from 'querystring'

import ClockIcon from 'components/Icons/Clock'
import DollarRoundedIcon from 'components/Icons/DollarRounded'
import MedalIcon from 'components/Icons/Medal'
import RoundedArrowDownIcon from 'components/Icons/RoundedArrowDown'
import RoundedArrowUpIcon from 'components/Icons/RoundedArrowUp'
import InfoHint from 'components/InfoHint'
import TooltipLeadsCustomers, {
  FilterType
} from 'components/TooltipLeadsCustomers'
import TooltipToReceive from 'components/TooltipToReceive'

import { formatMoney } from 'utils/FormatMoney'
import { handleDeleteQueryFilters } from 'utils/query-string'

import { theme } from 'styles/theme'

import * as S from './InfoList.styles'

export type InfoIcon = 'clock' | 'dollar' | 'medal' | 'arrowDown' | 'arrowUp'

export type PrevistoBreakdown = {
  leads: {
    total: number | string
    not_due: number | string
    due_up_to_7_days: number | string
    due_more_than_7_days: number | string
  }
  customers: {
    total: number | string
    not_due: number | string
    due_up_to_7_days: number | string
    due_more_than_7_days: number | string
  }
  churn: number | string
}

export type LeadsClientesBreakdown = {
  leads: number | string
  customers: number | string
  filterType?: FilterType
  cardTitle?: string
}

export type Info = {
  title: string | React.ReactNode
  subtitle: string
  /** Explicação do número, exibida num (i) ao lado do título. */
  hint?: string
  icon?: InfoIcon
  filter?: {
    name: string
    value: string
  }
  action?: () => void
  actionIcon?: Icons
  tooltipAction?: string
  // Suporte a hierarquia (deprecated - usar tooltip)
  children?: Info[]
  expandKey?: string
  // Tooltip rico para card Previsto (primeira linha)
  hasTooltipBreakdown?: boolean
  // Tooltip simples Leads/Clientes (segunda linha)
  leadsClientesBreakdown?: LeadsClientesBreakdown
}

export type InfoListProps = {
  isMobile?: boolean
  disableBoxShadow?: boolean
  baseFilters?: { [key in string]: string } | ParsedUrlQuery
  setBaseFilters?: (params: {
    [key in string]: string | number | object
  }) => void
  infos: Info[] | undefined
  type?: 'to_receive'
  bgColor?: string
  previstoBreakdown?: PrevistoBreakdown
  cardCount?: number
  /** Alias mantido para compatibilidade com as telas do SC-9544 (CashFlow/AccountsReceivable). */
  skeletonCardCount?: number
}

const icons = {
  clock: <ClockIcon />,
  dollar: <DollarRoundedIcon />,
  medal: <MedalIcon />,
  arrowDown: <RoundedArrowDownIcon />,
  arrowUp: <RoundedArrowUpIcon />
}

/**
 * Skeleton component for InfoList cards
 * Matches the styling of InfoList.styles Wrapper
 */
const InfoListSkeleton: React.FC<{
  cardCount?: number
  isToReceive?: boolean
  isExpanded?: boolean
}> = ({ cardCount = 4, isToReceive = false, isExpanded = false }) => {
  // Determine border radius based on state (matches Wrapper styles)
  const getBorderRadius = () => {
    if (isToReceive) return '0 0 12px 12px'
    if (isExpanded) return '12px 12px 0 0'
    return '12px'
  }

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: getBorderRadius(),
        border: `1px solid ${theme.palette.neutral[700]}`,
        ...(isToReceive && { borderTop: 'none' }),
        ...(isExpanded && { borderBottom: 'none' }),
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(100px, 1fr))',
          md: 'repeat(auto-fit, minmax(100px, 1fr))'
        },
        width: '100%'
      }}
    >
      {Array.from({ length: cardCount }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            padding: { xs: '20px 10px', md: '20px 27px' },
            rowGap: '4px',
            borderLeft:
              index === 0 ? 'none' : `1px solid ${theme.palette.neutral[700]}`,
            backgroundColor: 'transparent'
          }}
        >
          <Skeleton
            variant="text"
            width="70%"
            height={18}
            animation="wave"
            sx={{ bgcolor: theme.palette.neutral[700] }}
          />
          <Skeleton
            variant="text"
            width="85%"
            height={24}
            animation="wave"
            sx={{ bgcolor: theme.palette.neutral[600] }}
          />
        </Box>
      ))}
    </Box>
  )
}

const InfoList = ({
  isMobile,
  disableBoxShadow,
  infos,
  type,
  baseFilters,
  setBaseFilters,
  bgColor,
  previstoBreakdown,
  cardCount = 4,
  skeletonCardCount
}: InfoListProps) => {
  const truthFilters = ['nmrr', 'expiring']

  // Verifica se algum filtro de leads/clientes está ativo globalmente
  const allLeadsCustomersFilters = [
    'leads_not_due',
    'leads_due_up_to_7_days',
    'leads_due_more_than_7_days',
    'leads_churn',
    'customers_not_due',
    'customers_due_up_to_7_days',
    'customers_due_more_than_7_days',
    'customers_churn'
  ]
  const hasAnyLeadsCustomersFilterActive = allLeadsCustomersFilters.some(
    (filterName) => baseFilters?.[filterName] === 'true'
  )

  // Verifica se algum filtro da segunda linha (to_receive) está ativo
  const allToReceiveFilters = [
    'not_due',
    'overdue_less_than_7_days',
    'overdue_more_than_7_days',
    'churn',
    ...allLeadsCustomersFilters
  ]
  const hasAnyToReceiveFilterActive = allToReceiveFilters.some(
    (filterName) => baseFilters?.[filterName] === 'true'
  )
  const handleFilter = (name: string, value: string) => {
    if (!name || !value) return

    const filters = handleDeleteQueryFilters({
      filters: baseFilters ?? {},
      options: ['total', 'status', 'expiring', 'received', 'due_today']
    })

    // Limpa expand states (detailed/received_detailed) apenas ao clicar nos cards da primeira linha
    // Sub-cards (type=to_receive) devem manter o expand aberto
    const cleanedFilters = { ...filters } as { [key: string]: any }
    if (type !== 'to_receive') {
      delete cleanedFilters['detailed']
      delete cleanedFilters['received_detailed']
    }

    if (baseFilters && (baseFilters['to_receive'] || baseFilters['received'])) {
      const {
        due_date_greater_than,
        not_due,
        overdue_less_than_7_days,
        overdue_more_than_7_days,
        status,
        to_receive,
        received,
        nmrr,
        expiring,
        churn,
        due_today,
        // Limpar filtros de leads/clientes para garantir apenas um filtro por vez
        leads_not_due,
        leads_due_up_to_7_days,
        leads_due_more_than_7_days,
        leads_churn,
        customers_not_due,
        customers_due_up_to_7_days,
        customers_due_more_than_7_days,
        customers_churn,
        ...rest
      } = cleanedFilters
      !!setBaseFilters &&
        setBaseFilters(
          type !== 'to_receive'
            ? { query: { ...rest, [name]: value } }
            : { query: { ...rest, to_receive: true, [name]: value } }
        )
    } else {
      const {
        due_date_greater_than,
        not_due,
        overdue_less_than_7_days,
        overdue_more_than_7_days,
        status,
        to_receive,
        received,
        nmrr,
        expiring,
        churn,
        due_today,
        // Limpar filtros de leads/clientes para garantir apenas um filtro por vez
        leads_not_due,
        leads_due_up_to_7_days,
        leads_due_more_than_7_days,
        leads_churn,
        customers_not_due,
        customers_due_up_to_7_days,
        customers_due_more_than_7_days,
        customers_churn,
        ...rest
      } = cleanedFilters
      // `page: 0` porque trocar o recorte troca a lista inteira: quem estava na
      // página 5 e clicava num card recebia uma tabela vazia com o rodapé
      // "101–2 de 2" e o card do lado ainda marcando o valor cheio. O
      // `handleChangeRowsPerPage` da tabela já resetava; os handlers que
      // reescrevem o filtro carregavam a página adiante.
      !!setBaseFilters &&
        setBaseFilters({ query: { ...rest, [name]: value, page: 0 } })
    }
  }

  // Coleta todas as expandKeys dos infos para mutual exclusion
  const allExpandKeys =
    infos
      ?.filter((info) => info.expandKey)
      .map((info) => info.expandKey as string) || []

  // Helper para toggle de expandKey na query string (com mutual exclusion)
  const handleToggleExpand = (expandKey: string) => {
    if (!setBaseFilters || !baseFilters) return
    const currentValue = baseFilters[expandKey]
    const newQuery = { ...baseFilters }

    if (currentValue === 'true') {
      // Está fechando
      delete newQuery[expandKey]
    } else {
      // Está abrindo - fecha todos os outros (mutual exclusion)
      allExpandKeys.forEach((key) => {
        if (key !== expandKey) {
          delete newQuery[key]
        }
      })
      newQuery[expandKey] = 'true'
    }

    setBaseFilters({ query: newQuery })
  }

  // Helper para limpar expansão de um card
  const handleClearExpand = (expandKey: string) => {
    if (!setBaseFilters || !baseFilters) return
    const newQuery = { ...baseFilters }
    delete newQuery[expandKey]
    setBaseFilters({ query: newQuery })
  }

  // Verifica se um card está expandido
  const isCardExpanded = (expandKey?: string) => {
    if (!expandKey || !baseFilters) return false
    return baseFilters[expandKey] === 'true'
  }

  return infos?.length ? (
    <S.Wrapper
      isToReceive={type === 'to_receive'}
      bgColor={bgColor}
      disableBoxShadow={disableBoxShadow}
      isExpanded={
        baseFilters?.['detailed'] === 'true' ||
        baseFilters?.['received_detailed'] === 'true'
      }
    >
      {infos.map(
        (
          {
            subtitle,
            title,
            hint,
            icon,
            filter,
            action,
            actionIcon,
            tooltipAction,
            children,
            expandKey,
            hasTooltipBreakdown,
            leadsClientesBreakdown
          },
          index
        ) => {
          const cardIsExpanded = isCardExpanded(expandKey)
          const hasChildren = children && children.length > 0

          const cardKey = typeof title === 'string' ? title : `info-${index}`

          // Mapeia filterType para os nomes de filtro da API
          const apiFilterNames: Record<
            string,
            { lead: string; customer: string }
          > = {
            not_due: {
              lead: 'leads_not_due',
              customer: 'customers_not_due'
            },
            overdue_less_than_7_days: {
              lead: 'leads_due_up_to_7_days',
              customer: 'customers_due_up_to_7_days'
            },
            overdue_more_than_7_days: {
              lead: 'leads_due_more_than_7_days',
              customer: 'customers_due_more_than_7_days'
            },
            churn: {
              lead: 'leads_churn',
              customer: 'customers_churn'
            }
          }

          // Verifica se há filtro de leads ou clientes ativo para este card
          const cardFilterType = leadsClientesBreakdown?.filterType
          const leadFilterName = cardFilterType
            ? apiFilterNames[cardFilterType]?.lead
            : undefined
          const customerFilterName = cardFilterType
            ? apiFilterNames[cardFilterType]?.customer
            : undefined

          const isLeadFilterActive =
            leadFilterName && baseFilters?.[leadFilterName] === 'true'
          const isCustomerFilterActive =
            customerFilterName && baseFilters?.[customerFilterName] === 'true'

          const activeCustomerType: 'lead' | 'customer' | null =
            isLeadFilterActive
              ? 'lead'
              : isCustomerFilterActive
              ? 'customer'
              : null

          // Determina o valor a ser exibido baseado no filtro ativo
          const displaySubtitle =
            activeCustomerType && leadsClientesBreakdown
              ? activeCustomerType === 'lead'
                ? typeof leadsClientesBreakdown.leads === 'number'
                  ? formatMoney(leadsClientesBreakdown.leads)
                  : leadsClientesBreakdown.leads
                : typeof leadsClientesBreakdown.customers === 'number'
                ? formatMoney(leadsClientesBreakdown.customers)
                : leadsClientesBreakdown.customers
              : subtitle

          // Label para indicar o tipo de filtro ativo
          const customerTypeLabel = activeCustomerType
            ? activeCustomerType === 'lead'
              ? 'Leads'
              : 'Clientes'
            : null

          const cardContent = (
            <S.Content
              isMobile={isMobile}
              onClick={() => {
                // Se tem children e expandKey, toggle a expansão
                if (hasChildren && expandKey) {
                  handleToggleExpand(expandKey)
                  return
                }
                // Comportamento original de filtro
                if (
                  (filter?.name === 'tab' &&
                    baseFilters &&
                    baseFilters[filter?.name] === 'recorrencias') ||
                  (baseFilters &&
                    filter &&
                    baseFilters[filter?.name] !== filter?.value) ||
                  !filter
                ) {
                  if (filter?.name && filter?.value) {
                    handleFilter(filter.name, filter.value)
                  }
                }
              }}
              isSelected={
                (filter &&
                  baseFilters &&
                  baseFilters[filter.name] === filter.value &&
                  !(
                    filter.name === 'tab' && filter.value === 'recorrencias'
                  )) ||
                !!activeCustomerType ||
                // Card "Previsto" (to_receive) também fica selecionado quando algum filtro da segunda linha está ativo
                (filter?.name === 'to_receive' &&
                  baseFilters?.['detailed'] === 'true' &&
                  hasAnyToReceiveFilterActive)
              }
              isExpanded={cardIsExpanded}
              canFilter={
                hasChildren ||
                (filter?.name === 'tab' &&
                  baseFilters &&
                  baseFilters[filter?.name] === 'recorrencias') ||
                (baseFilters &&
                  filter &&
                  baseFilters[filter.name] !== filter.value)
              }
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                minWidth={0}
              >
                <Stack minWidth={0}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap="6px"
                    minWidth={0}
                  >
                    <S.Heading
                      title={typeof title === 'string' ? title : undefined}
                    >
                      {title}
                    </S.Heading>
                    {hint && <InfoHint title={hint} />}
                    {customerTypeLabel && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: theme.palette.neutral[100],
                          bgcolor:
                            activeCustomerType === 'lead'
                              ? theme.palette.secondary[300]
                              : theme.palette.primary.main,
                          px: '6px',
                          py: '2px',
                          borderRadius: '4px',
                          lineHeight: 1.2
                        }}
                      >
                        {customerTypeLabel}
                      </Box>
                    )}
                  </Stack>

                  <S.SubtitleWrapper>
                    {icon && icons[icon]}
                    <S.Subtitle
                      title={
                        typeof displaySubtitle === 'string'
                          ? displaySubtitle
                          : undefined
                      }
                    >
                      {displaySubtitle}
                    </S.Subtitle>
                  </S.SubtitleWrapper>
                </Stack>
                <Stack
                  mt="-3px"
                  sx={{
                    cursor: 'pointer'
                  }}
                  alignItems="center"
                  gap="2px"
                >
                  {((filter &&
                    filter.name !== 'tab' &&
                    baseFilters &&
                    (baseFilters[filter.name] === filter.value ||
                      (truthFilters.includes(filter.name) &&
                        baseFilters[filter.name]))) ||
                    activeCustomerType) && (
                    <Tooltip title="Limpar filtro" placement="top">
                      <div>
                        <Icon
                          onClick={(e) => {
                            e.stopPropagation()
                            if (setBaseFilters && filter?.name) {
                              const {
                                [filter.name]: _,
                                to_receive,
                                received,
                                due_date_greater_than,
                                not_due,
                                overdue_less_than_7_days,
                                overdue_more_than_7_days,
                                status,
                                expiring,
                                nmrr,
                                churn,
                                customer_type,
                                detailed,
                                received_detailed,
                                leads_not_due,
                                leads_due_up_to_7_days,
                                leads_due_more_than_7_days,
                                leads_churn,
                                customers_not_due,
                                customers_due_up_to_7_days,
                                customers_due_more_than_7_days,
                                customers_churn,
                                ...restFilters
                              } = baseFilters as { [key: string]: any }

                              setBaseFilters(
                                type !== 'to_receive'
                                  ? { query: restFilters }
                                  : {
                                      query: {
                                        ...restFilters,
                                        to_receive: true
                                      }
                                    }
                              )
                            }
                          }}
                          icon="close"
                          colorSX={theme.palette.neutral[600]}
                          size={2.5}
                        />
                      </div>
                    </Tooltip>
                  )}
                  {/* Botão limpar para cards expandidos com expandKey */}
                  {hasChildren && expandKey && cardIsExpanded && (
                    <Tooltip title="Fechar detalhes" placement="top">
                      <div>
                        <Icon
                          onClick={(e) => {
                            e.stopPropagation()
                            handleClearExpand(expandKey)
                          }}
                          icon="close"
                          colorSX={theme.palette.neutral[600]}
                          size={2.5}
                        />
                      </div>
                    </Tooltip>
                  )}
                  {/* Chevron para cards com children */}
                  {hasChildren && expandKey && (
                    <Tooltip
                      placement="bottom"
                      title={cardIsExpanded ? 'Minimizar' : 'Expandir detalhes'}
                    >
                      <Stack
                        onClick={(e) => {
                          e.stopPropagation()
                          handleToggleExpand(expandKey)
                        }}
                        bgcolor={theme.palette.neutral[800]}
                        borderRadius="8px"
                        width="24px"
                        height="24px"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon
                          colorSX={
                            cardIsExpanded
                              ? theme.palette.primary.main
                              : theme.palette.neutral[600]
                          }
                          icon={
                            cardIsExpanded ? 'faChevronUp' : 'faChevronDown'
                          }
                          size={2}
                        />
                      </Stack>
                    </Tooltip>
                  )}
                  {/* Botão limpar para card Previsto quando há filtros de leads/clientes ativos */}
                  {actionIcon &&
                    !hasChildren &&
                    baseFilters &&
                    baseFilters['detailed'] === 'true' &&
                    hasAnyLeadsCustomersFilterActive && (
                      <Tooltip title="Limpar todos os filtros" placement="top">
                        <div>
                          <Icon
                            onClick={(e) => {
                              e.stopPropagation()
                              if (setBaseFilters) {
                                const {
                                  detailed,
                                  leads_not_due,
                                  leads_due_up_to_7_days,
                                  leads_due_more_than_7_days,
                                  leads_churn,
                                  customers_not_due,
                                  customers_due_up_to_7_days,
                                  customers_due_more_than_7_days,
                                  customers_churn,
                                  not_due,
                                  overdue_less_than_7_days,
                                  overdue_more_than_7_days,
                                  churn,
                                  to_receive,
                                  ...restFilters
                                } = baseFilters as { [key: string]: any }

                                setBaseFilters({
                                  query: { ...restFilters, page: 0 }
                                })
                              }
                            }}
                            icon="close"
                            colorSX={theme.palette.neutral[600]}
                            size={2.5}
                          />
                        </div>
                      </Tooltip>
                    )}
                  {/* ActionIcon para cards com expand (Previsto e Recebido) */}
                  {actionIcon &&
                    !hasChildren &&
                    baseFilters &&
                    (() => {
                      // Determina qual query param controla este card
                      const isRecebido = title === 'Recebido'
                      const expandParam = isRecebido
                        ? 'received_detailed'
                        : 'detailed'
                      const isExpanded = baseFilters[expandParam] === 'true'

                      return (
                        <Tooltip
                          placement="bottom"
                          title={
                            tooltipAction
                              ? isExpanded
                                ? 'Minimizar detalhes'
                                : tooltipAction
                              : ''
                          }
                        >
                          <Stack
                            onClick={(e) => {
                              e.stopPropagation()
                              if (action) {
                                action()
                              }
                            }}
                            bgcolor={theme.palette.neutral[800]}
                            borderRadius="8px"
                            width="24px"
                            height="24px"
                            alignItems="center"
                            mt={
                              baseFilters['tab'] === 'recorrencias' ||
                              baseFilters['detailed'] ||
                              baseFilters['received_detailed']
                                ? filter && !baseFilters[filter.name]
                                  ? 'auto'
                                  : '-4px'
                                : 'auto'
                            }
                            justifyContent="center"
                          >
                            <Icon
                              colorSX={theme.palette.neutral[600]}
                              icon={
                                isExpanded
                                  ? 'faChevronUp'
                                  : (actionIcon as Icons)
                              }
                              size={2}
                            />
                          </Stack>
                        </Tooltip>
                      )
                    })()}
                </Stack>
              </Stack>
            </S.Content>
          )

          // Se o card tem tooltip de breakdown completo (Previsto na primeira linha)
          if (hasTooltipBreakdown && previstoBreakdown) {
            return (
              <TooltipToReceive
                key={cardKey}
                leads={previstoBreakdown.leads}
                customers={previstoBreakdown.customers}
                churn={previstoBreakdown.churn}
              >
                {cardContent}
              </TooltipToReceive>
            )
          }

          // Se o card tem tooltip simples Leads/Clientes (segunda linha)
          if (leadsClientesBreakdown) {
            const handleTooltipFilterClick = (
              filterType: string,
              customerType: 'lead' | 'customer'
            ) => {
              if (!setBaseFilters || !baseFilters) return

              // Mapeia o filterType para o nome do filtro da API
              const filterNameMap: Record<string, Record<string, string>> = {
                not_due: {
                  lead: 'leads_not_due',
                  customer: 'customers_not_due'
                },
                overdue_less_than_7_days: {
                  lead: 'leads_due_up_to_7_days',
                  customer: 'customers_due_up_to_7_days'
                },
                overdue_more_than_7_days: {
                  lead: 'leads_due_more_than_7_days',
                  customer: 'customers_due_more_than_7_days'
                },
                churn: {
                  lead: 'leads_churn',
                  customer: 'customers_churn'
                }
              }

              const apiFilterName = filterNameMap[filterType]?.[customerType]
              if (!apiFilterName) return

              // Limpa filtros anteriores de status/período e aplica o novo
              const {
                not_due,
                overdue_less_than_7_days,
                overdue_more_than_7_days,
                customer_type,
                status,
                to_receive,
                churn,
                leads_not_due,
                leads_due_up_to_7_days,
                leads_due_more_than_7_days,
                leads_churn,
                customers_not_due,
                customers_due_up_to_7_days,
                customers_due_more_than_7_days,
                customers_churn,
                ...rest
              } = baseFilters as { [key: string]: any }

              const newQuery = {
                ...rest,
                [apiFilterName]: 'true',
                page: 0
              }

              setBaseFilters({ query: newQuery })
            }

            // Mobile: mostra breakdown abaixo do card em vez de tooltip
            if (isMobile) {
              return (
                <Box
                  key={cardKey}
                  sx={{ display: 'flex', flexDirection: 'column' }}
                >
                  {cardContent}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      px: '10px',
                      pb: '10px',
                      mt: '-8px'
                    }}
                  >
                    <Box
                      onClick={(e) => {
                        e.stopPropagation()
                        handleTooltipFilterClick(
                          leadsClientesBreakdown.filterType || '',
                          'lead'
                        )
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        bgcolor: isLeadFilterActive
                          ? theme.palette.secondary[300]
                          : theme.palette.neutral[800],
                        borderRadius: '4px',
                        px: '6px',
                        py: '2px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: theme.palette.secondary[300]
                        }
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: '10px',
                          color: isLeadFilterActive
                            ? theme.palette.neutral[100]
                            : theme.palette.neutral[400]
                        }}
                      >
                        Leads:
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: isLeadFilterActive
                            ? theme.palette.neutral[100]
                            : theme.palette.neutral[300]
                        }}
                      >
                        {typeof leadsClientesBreakdown.leads === 'number'
                          ? formatMoney(leadsClientesBreakdown.leads)
                          : leadsClientesBreakdown.leads}
                      </Box>
                    </Box>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation()
                        handleTooltipFilterClick(
                          leadsClientesBreakdown.filterType || '',
                          'customer'
                        )
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        bgcolor: isCustomerFilterActive
                          ? theme.palette.primary.main
                          : theme.palette.neutral[800],
                        borderRadius: '4px',
                        px: '6px',
                        py: '2px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: theme.palette.primary.main
                        }
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: '10px',
                          color: isCustomerFilterActive
                            ? theme.palette.neutral[100]
                            : theme.palette.neutral[400]
                        }}
                      >
                        Clientes:
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: isCustomerFilterActive
                            ? theme.palette.neutral[100]
                            : theme.palette.neutral[300]
                        }}
                      >
                        {typeof leadsClientesBreakdown.customers === 'number'
                          ? formatMoney(leadsClientesBreakdown.customers)
                          : leadsClientesBreakdown.customers}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )
            }

            // Desktop: usa tooltip
            return (
              <TooltipLeadsCustomers
                key={cardKey}
                leads={leadsClientesBreakdown.leads}
                customers={leadsClientesBreakdown.customers}
                filterType={leadsClientesBreakdown.filterType}
                cardTitle={leadsClientesBreakdown.cardTitle}
                onFilterClick={handleTooltipFilterClick}
              >
                {cardContent}
              </TooltipLeadsCustomers>
            )
          }

          return <React.Fragment key={cardKey}>{cardContent}</React.Fragment>
        }
      )}
    </S.Wrapper>
  ) : type === 'to_receive' ? (
    <InfoListSkeleton
      isToReceive
      cardCount={skeletonCardCount ?? cardCount ?? 5}
    />
  ) : (
    <InfoListSkeleton
      cardCount={skeletonCardCount ?? cardCount ?? 4}
      isExpanded={
        baseFilters?.['detailed'] === 'true' ||
        baseFilters?.['received_detailed'] === 'true'
      }
    />
  )
}

export default InfoList
